import type { AssistantExternalToolManifest } from '@thinkalpha/platform-ws-client/contracts/assistant.js';
import { tradingViewChartIntervals } from '@thinkalpha/ui-types/contracts/workspace';
import type { EventType, WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import { z } from 'zod';

export const addWidgetToolName = 'addWidget';
export const queryHelpCenterToolName = 'queryHelpCenter';

export const validAddWidgetEventTypes = [
    'dividend',
    'split',
    'ipo',
    'symbol-change',
    'primary-exchange-change',
    'earnings',
    'acquisition',
    'buyback',
    'shares-outstanding-change',
] as const satisfies readonly EventType[];

const genericAddWidgetTypes = [
    'accounts',
    'backtest',
    'basket-trading',
    'executions',
    'lab',
    'locates',
    'manual-review',
    'messages',
    'notepad',
    'order-blotter',
    'orders',
    'positions',
    'table',
    'trade-pro',
    'trade-pro-multi',
] as const satisfies readonly WidgetType[];

export const addWidgetInputSchema = z.discriminatedUnion('widgetType', [
    z.strictObject({
        widgetType: z.literal('alerts'),
        activeAlertsTab: z.enum(['symbols', 'ideas']).optional(),
    }),
    z.strictObject({
        widgetType: z.literal('alpha-eye'),
        symbol: z.string().min(1),
    }),
    z.strictObject({
        widgetType: z.literal('events'),
        symbolFilter: z.string().min(1).optional(),
        eventTypes: z.array(z.enum(validAddWidgetEventTypes)).optional(),
    }),
    z.strictObject({
        widgetType: z.literal('filings'),
        symbolFilter: z.string().min(1),
        filingTypes: z.array(z.string().min(1)).optional(),
    }),
    z.strictObject({
        widgetType: z.literal('news'),
        symbolFilter: z.string().min(1).optional(),
        ideaId: z.string().min(1).optional(),
    }),
    z.strictObject({
        widgetType: z.literal('searchalpha'),
        ideaId: z.string().min(1),
    }),
    z.strictObject({
        widgetType: z.literal('tradingview-chart'),
        symbol: z.string().min(1),
        interval: z.enum(tradingViewChartIntervals).optional(),
    }),
    z.strictObject({
        widgetType: z.literal('results'),
        ideaId: z.string().min(1).optional(),
    }),
    z.strictObject({
        widgetType: z.literal('scanner'),
        ideaId: z.string().min(1).optional(),
    }),
    z.strictObject({
        widgetType: z.literal('screener'),
        ideaId: z.string().min(1).optional(),
    }),
    z.strictObject({
        widgetType: z.literal('watchlist'),
        ideaId: z.string().min(1).optional(),
    }),
    z.strictObject({
        widgetType: z.enum(genericAddWidgetTypes),
    }),
]);

export type AddWidgetInput = z.infer<typeof addWidgetInputSchema>;

export const addWidgetToolOutputSchema = z.discriminatedUnion('success', [
    z.strictObject({
        success: z.literal(true),
        widgetType: z.string().min(1),
        activeAlertsTab: z.enum(['symbols', 'ideas']).optional(),
        eventTypes: z.array(z.enum(validAddWidgetEventTypes)).optional(),
        filingTypes: z.array(z.string().min(1)).optional(),
        ideaId: z.string().min(1).optional(),
        interval: z.enum(tradingViewChartIntervals).optional(),
        symbol: z.string().min(1).optional(),
        symbolFilter: z.string().min(1).optional(),
    }),
    z.strictObject({
        success: z.literal(false),
        error: z.string().min(1).optional(),
    }),
]);

export type AddWidgetToolOutput = z.infer<typeof addWidgetToolOutputSchema>;

const addWidgetToolSchemaWidgetTypes = [
    'alerts',
    'alpha-eye',
    'events',
    'filings',
    'news',
    'searchalpha',
    'tradingview-chart',
    'results',
    'scanner',
    'screener',
    'watchlist',
    ...genericAddWidgetTypes,
] as const satisfies readonly WidgetType[];

// Per-widget required fields cannot be represented with top-level oneOf/anyOf/allOf because Anthropic rejects them.
// Keep this flat provider schema in sync with the stricter addWidgetInputSchema execution validator.
const addWidgetExternalToolInputSchema = {
    type: 'object',
    additionalProperties: false,
    properties: {
        widgetType: {
            type: 'string',
            enum: addWidgetToolSchemaWidgetTypes,
            description: 'Widget type to add or open in the active workspace.',
        },
        symbol: {
            type: 'string',
            minLength: 1,
            description: 'Ticker symbol for symbol-centric widgets such as tradingview-chart and alpha-eye.',
        },
        interval: {
            type: 'string',
            enum: tradingViewChartIntervals,
            description: 'Optional chart interval for tradingview-chart.',
        },
        symbolFilter: {
            type: 'string',
            minLength: 1,
            description: 'Ticker symbol filter for news, events, or filings widgets.',
        },
        eventTypes: {
            type: 'array',
            items: { type: 'string', enum: validAddWidgetEventTypes },
            description: 'Event categories to show when opening an events widget.',
        },
        filingTypes: {
            type: 'array',
            items: { type: 'string', minLength: 1 },
            description: 'Filing type filters when opening a filings widget, such as 10-K, 10-Q, or 8-K.',
        },
        activeAlertsTab: {
            type: 'string',
            enum: ['symbols', 'ideas'],
            description: 'Initial tab when opening alerts.',
        },
        ideaId: {
            type: 'string',
            minLength: 1,
            description: 'Saved idea ID for widgets that support opening an existing idea.',
        },
    },
    required: ['widgetType'],
} as const satisfies AssistantExternalToolManifest['inputSchema'];

export const createAddWidgetToolSuccessOutput = (input: AddWidgetInput): AddWidgetToolOutput => {
    switch (input.widgetType) {
        case 'alerts':
            return {
                success: true,
                widgetType: input.widgetType,
                ...(input.activeAlertsTab ? { activeAlertsTab: input.activeAlertsTab } : {}),
            };
        case 'events':
            return {
                success: true,
                widgetType: input.widgetType,
                ...(input.symbolFilter ? { symbolFilter: input.symbolFilter } : {}),
                ...(input.eventTypes?.length ? { eventTypes: input.eventTypes } : {}),
            };
        case 'filings':
            return {
                success: true,
                widgetType: input.widgetType,
                symbolFilter: input.symbolFilter,
                ...(input.filingTypes?.length ? { filingTypes: input.filingTypes } : {}),
            };
        case 'news':
            return {
                success: true,
                widgetType: input.widgetType,
                ...(input.ideaId ? { ideaId: input.ideaId } : {}),
                ...(input.symbolFilter ? { symbolFilter: input.symbolFilter } : {}),
            };
        case 'alpha-eye':
            return { success: true, widgetType: input.widgetType, symbol: input.symbol };
        case 'searchalpha':
            return { success: true, widgetType: input.widgetType, ideaId: input.ideaId };
        case 'tradingview-chart':
            return {
                success: true,
                widgetType: input.widgetType,
                symbol: input.symbol,
                ...(input.interval ? { interval: input.interval } : {}),
            };
        case 'results':
        case 'scanner':
        case 'screener':
        case 'watchlist':
            return {
                success: true,
                widgetType: input.widgetType,
                ...(input.ideaId ? { ideaId: input.ideaId } : {}),
            };
        default:
            return { success: true, widgetType: input.widgetType };
    }
};

export const addWidgetExternalTool: AssistantExternalToolManifest = {
    name: addWidgetToolName,
    description:
        'Request the client to add a widget to the active workspace. ' +
        'When the user asks to add, open, or create a widget, call this tool with the appropriate widgetType. ' +
        'For tradingview-chart, always pass the requested ticker in symbol and optionally interval if the user requests a specific chart timeframe. ' +
        'For alpha-eye, always pass the requested ticker in symbol. ' +
        'For news widgets, include symbolFilter when the user mentions a company or ticker. ' +
        'For events widgets, pass symbolFilter for the ticker and eventTypes to scope which events are shown; for corporate actions, pass all event types except earnings. ' +
        'For filings, pass symbolFilter and filingTypes when the user asks for specific filing categories such as 10-K, 10-Q, or 8-K. ' +
        'For alerts, use activeAlertsTab symbols for stock or price alerts and ideas only when the user explicitly wants the idea alerts tab. ' +
        'Use ideaId only for widgets that support opening a saved idea, such as searchalpha, results, scanner, screener, watchlist, or news. ' +
        'For searchalpha, pass ideaId; do not open searchalpha without an ideaId.',
    inputSchema: addWidgetExternalToolInputSchema,
};

export const queryHelpCenterInputSchema = z.strictObject({
    query: z.string().min(2),
});

const queryHelpCenterExternalToolInputSchema = {
    type: 'object',
    additionalProperties: false,
    properties: {
        query: {
            type: 'string',
            minLength: 2,
            description: 'Natural-language search query for the in-app help center.',
        },
    },
    required: ['query'],
} as const satisfies AssistantExternalToolManifest['inputSchema'];

const helpCenterSearchResultSchema = z.discriminatedUnion('type', [
    z.strictObject({
        type: z.literal('document'),
        id: z.string().min(1),
        title: z.string().min(1),
        path: z.array(z.string()),
        snippet: z.string(),
    }),
    z.strictObject({
        type: z.literal('guide'),
        id: z.string().min(1),
        title: z.string().min(1),
        path: z.array(z.string()),
        stepTitle: z.string().min(1).optional(),
        snippet: z.string(),
    }),
]);

export const queryHelpCenterToolOutputSchema = z.discriminatedUnion('success', [
    z.strictObject({
        success: z.literal(true),
        query: z.string().min(2),
        results: z.array(helpCenterSearchResultSchema),
    }),
    z.strictObject({
        success: z.literal(false),
        error: z.string().min(1).optional(),
    }),
]);

export type QueryHelpCenterToolOutput = z.infer<typeof queryHelpCenterToolOutputSchema>;

export const queryHelpCenterExternalTool: AssistantExternalToolManifest = {
    name: queryHelpCenterToolName,
    description:
        'Search the client help center for platform documentation and workflow guidance. ' +
        'Use this when the user asks how to use the platform, asks about available features, asks for help center or documentation content, or needs guidance on workflows documented by the UI help center. ' +
        'Pass a concise natural-language search query based on the user question. ' +
        'This tool only returns compact matching snippets; use those snippets to answer the user and do not claim to open documents or start guides.',
    inputSchema: queryHelpCenterExternalToolInputSchema,
};

export const assistantExternalTools = [addWidgetExternalTool, queryHelpCenterExternalTool];
